import { Namespace, ParseKeys, TFunction, TOptions, t } from "i18next";
import { PropsWithChildren } from "react";
import { toast, ToastBar, Toaster, ToastOptions } from "react-hot-toast";

type SimplifiedToastOptions = Pick<
  ToastOptions,
  "duration" | "id" | "position"
>;
interface ToastService {
  toastSuccess(text: string, options?: SimplifiedToastOptions): void;
  toastError(text: string, options?: SimplifiedToastOptions): void;
}

const toastSuccess: ToastService["toastSuccess"] = (title, options) => {
  toast.success(title, {
    ...options,
    duration: options?.duration ?? 5000,
  });
};
const toastError: ToastService["toastError"] = (title, options) => {
  toast.error(title, {
    ...options,
    duration: options?.duration ?? 30000,
  });
};

export const ProvideToasts = ({ children }: PropsWithChildren) => {
  return (
    <>
      <Toaster position="top-right">
        {(t) => (
          <div
            style={{ cursor: "pointer" }}
            onClick={() => toast.dismiss(t.id)}
          >
            <ToastBar toast={t} />
          </div>
        )}
      </Toaster>
      {children}
    </>
  );
};

type ToastServiceWithIntl<Ns extends Namespace, KPrefix = undefined> = Record<
  keyof ToastService,
  <
    const Key extends ParseKeys<Ns, TOpt, KPrefix> | TemplateStringsArray,
    const TOpt extends TOptions,
    TFun = TFunction<Ns, KPrefix>,
    ActualOptions = TFun extends (...args: any[]) => any
      ? Parameters<TFun>[1]
      : never,
  >(
    text: Key | Key[],
    options?: ActualOptions & SimplifiedToastOptions,
    defaultValue?: string,
  ) => void
>;

// eslint-disable-next-line react-refresh/only-export-components
export function toastsWithIntl<
  Ns extends Namespace,
  KPrefix = undefined,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
>(_ns: Ns): ToastServiceWithIntl<Ns, KPrefix> {
  return Object.fromEntries(
    Object.entries({
      toastSuccess,
      toastError,
    }).map(([key, service]) => [
      key,
      (text, options, defaultValue) => {
        return service(
          (defaultValue
            ? (t as any)(text, defaultValue, options)
            : (t as any)(
                // BUG: Cannot find a Typescript solution to solve this mapping without a any
                text as any,
                options,
              )) as string,
          options,
        );
      },
    ]),
  ) as ToastServiceWithIntl<Ns, KPrefix>;
}
